import {
  ClaimableWarning,
  ClaimFileType,
  ClaimStatus,
  ClaimSubmissionInfo,
  ClaimType,
  Deadline,
} from '../components/parcel-finder/claims/types';

export type CreateReturnShipmentsPayload = {
  orderId: string;
  count: number;
};

export type ReturnShipment = {
  id: number;
  trackingCode: string;
  preliminaryCode: string | null;
  labelId: number;
  labelUrl: string;
};

export type ReturnCarrierService = {
  id: string;
  name: string;
  carrierCountry: string;
  carrierName: string;
  carrierCode: string;
  carrierId: string;
};

export type GetClaimsOptions = {
  shopId: number;
  [key: string]: unknown;
};

export enum ConsumerActionStatus {
  COMPLETED = 'completed',
  PENDING = 'pending',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
}

export type ListClaim = {
  id: number;
  claimType: ClaimType;
  status: ClaimStatus;
  trackingCode: string;
  shipmentId: number | null;
  shopOrderId: string | null;
  shopId: number;
  carrierCode: string;
  carrierCountryCode: string;
  comment: string | null;
  damageDescription: string | null;
  deliveryDate: string | null;
  firstHubScanDate: string | null;
  isReturnShipment: boolean | null;
  isNon7sShipment: boolean | null;
  netValue: number;
  netValueCurrency: string;
  ssHubScanDate: string | null;
  recipientName: string | null;
  deliveryAttemptDate: string | null;
  weight: number | null;
  customerResponseId: number | null;
  createdAt: string;
  rejectedAt: string | null;
  isDeclinedPastTakeActionDeadline: boolean;
  isCancellableByCustomer: boolean;
  deadlineLastSubmissionDate: string | null;
  submittedAt: string | null;
  consumerActionStatus: ConsumerActionStatus | null;
  recipientCountryCode: string | null;
};

export type Claim = ListClaim & {
  deliveredToMailboxDate: string | null;
  postalReturnDate: string | null;
  postalReturnDeliveryDate: string | null;
  shipmentCreationDate: string | null;
  customerComment: string | null;
  recipientAddress: string | null;
  recipientCity: string | null;
  recipientZipCode: string | null;
  recipientCountryCode: string | null;
  recipientEmail: string | null;
  recipientPhoneNumber: string | null;
  outsideOfDeadlineReason: string | null;
  shopClaimId: string | null;
  isMultiShipmentOrder: boolean | null;
  warehouseDeliveryDate: string | null;
  lmcCaseId: string | null;
  missingItemDescription: string | null;
  cashOnDeliveryPaymentType: CashOnDeliveryPaymentType | null;
  isTaxIncluded: boolean;
  taxRate: number | null;
  orderItems: Array<ClaimOrderItem>;
  files: Array<ClaimFile>;
  deadline: Deadline;
  claimableWarning: ClaimableWarning;
  consumerActionStatus: ConsumerActionStatus | null;
};

export type GetClaimsResponse = {
  claims: Array<ListClaim>;
  offset: number;
  limit: number;
  total: number;
};

export type ClaimFile = {
  id: number;
  name: string;
  mimeType: string;
  fileType: ClaimFileType;
  size: number;
  publicUrl: string;
};

export type ClaimOrderItem = {
  id: number;
  description: string | null;
  quantity: number | null;
  unitPrice: number | null;
  taxRate: number | null;
};

export type CashOnDeliveryPaymentType = 'full' | 'partial';

export type PostClaimsImportInvoicePayload = {
  trackingCode: string;
  shopOrderId: string | null | undefined;
};

export type PostClaimsImportDORPayload = {
  shipmentId: number;
};

export type ImportFileResponse = { status: 'found'; file: ClaimFile } | { status: 'not_found'; file: null };

export type GetInvoiceExtractionResponse = {
  netValueCurrency: string | null;
  netValue: number | null;
  grossValue: number | null;
  taxRate: number | null;
  orderItems: Array<{
    name: string | null;
    quantity: number | null;
    price: number | null;
    taxRate: number | null;
  }>;
};

export type CreateDorSigningLinkResponse = {
  signingLink: string;
};

export type CreatePictureUploadLinkResponse = {
  pictureUploadLink: string;
};

export type Shipment = {
  id: number;
  status: string;
  trackingCode: string;
  referenceNumber: string | null;
  creationDate: string;
  comment: string | null;
  trackingPageUrl: string | null;
  carrierTrackingUrl: string;
  tags: Array<{
    id: number;
    name: string;
    content: string;
  }>;
  isTrackable: boolean;
  weight: number | null;
  isReturnShipment: boolean;
  pickupPointSelected: boolean;
  plannedPickupDate: string | null;
  carrierService: string;
  deliveryDate: string | null;
  postalReturnDeliveryDate: string | null;
  postalReturnDate: string | null;
  deliveryAttemptDate: string | null;
  deliveredToMailboxDate: string | null;
  firstHubScanDate: string | null;
  ssHubScanDate: string | null;
  order: {
    id: number;
    shopOrderId: string;
    state: string;
    recipient: ShipmentRecipient;
  };
  customer: ShipmentRecipient;
  carrier: {
    id: number;
    code: string;
    name: string;
    country: string;
    countryName: string;
    countryCode: string;
    logoUrl: string;
  };
  events: Array<unknown>;
  lastEvent: LastEvent;
};

export type ShipmentRecipient = {
  name: string | null;
  countryName: string | null;
  countryCode: string | null;
  city: string | null;
  zipCode: string | null;
  address: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
};

export type ClaimsCounts = {
  carriers: Record<string, number>;
  recipientCountries: Record<string, number>;
};

export type ClaimableShipment = Shipment & {
  claimable: boolean;
  claimId: number | null;
  claimStatus: ClaimStatus | null;
  claimType: ClaimType | null;
  consumerActionStatus: ConsumerActionStatus | null;
  nonClaimableReasonMessage: string | null;
  nonClaimableReasonCode: string | null;
  claimSubmissionInfo: ClaimSubmissionInfo;
  claimSubmittedAt: string | null;
  availableDORLanguages: string[];
  claimRequirements: ClaimRequirements | null;
};

export type SubmitClaimResponse = {
  id: number;
};

export type ClaimRequirements = {
  isPicturesOfMissingItemRequired: boolean;
  isDamagePhotosRequired: boolean;
  declarationOfRecipient: 'required' | 'optional' | 'none';
  dataProcessingConsent: 'required' | 'none';
};

export type PostClaimRequirementsPayload = {
  carrierCode: string;
  carrierCountryCode: string;
  deliveryDate: string | null;
  postalReturnDeliveryDate: string | null;
  deliveredToMailboxDate: string | null;
  isReturnShipment: boolean;
};

export type LastEvent = {
  meaning: string;
  type: string;
};
